import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, Mail, Phone, MapPin, Globe, Save, Plus, Trash, Brain } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

type Profile = {
  id: string;
  name: string;
  role: string;
  bio: string;
  experience_level: 'beginner' | 'intermediate' | 'advanced';
  career_goals: string;
  skills: string[];
  email: string;
  phone?: string;
  location?: string;
  website?: string;
  linkedin?: string;
  created_at: string;
  updated_at: string;
};

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<Partial<Profile>>({
    name: user?.user_metadata?.name || '',
    role: user?.user_metadata?.role || 'product-manager',
    bio: '',
    experience_level: 'beginner',
    career_goals: '',
    skills: [],
    email: user?.email || '',
  });
  const [newSkill, setNewSkill] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      if (!user?.id) {
        throw new Error('No user ID found');
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      if (data) {
        setProfile({
          ...data,
          name: data.name || user.user_metadata?.name || '',
          role: data.role || user.user_metadata?.role || 'product-manager',
          email: user.email || '',
          skills: data.skills || [],
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      if (!user?.id) {
        throw new Error('No user ID found');
      }

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...profile,
          updated_at: new Date().toISOString(),
        });

      if (error) {
        throw error;
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Error al guardar el perfil. Por favor, inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !profile.skills?.includes(newSkill.trim())) {
      setProfile({
        ...profile,
        skills: [...(profile.skills || []), newSkill.trim()],
      });
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setProfile({
      ...profile,
      skills: profile.skills?.filter(skill => skill !== skillToRemove) || [],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-foreground">Perfil Profesional</h1>
            <Brain className="h-8 w-8 text-accent" />
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-400 text-green-700">
              Perfil actualizado correctamente
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="dark-card p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Información Personal</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground/80">
                    Nombre completo
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-foreground/80">
                    Rol profesional
                  </label>
                  <div className="mt-1">
                    <select
                      id="role"
                      value={profile.role}
                      onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="product-manager">Product Manager</option>
                      <option value="developer">Desarrollador</option>
                      <option value="entrepreneur">Emprendedor</option>
                      <option value="consultant">Consultor</option>
                      <option value="project-manager">Project Manager</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-foreground/80">
                    Biografía profesional
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="bio"
                      rows={4}
                      value={profile.bio}
                      onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="Cuéntanos sobre tu experiencia y aspiraciones profesionales..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="dark-card p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Experiencia y Objetivos</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="experience_level" className="block text-sm font-medium text-foreground/80">
                    Nivel de experiencia
                  </label>
                  <div className="mt-1">
                    <select
                      id="experience_level"
                      value={profile.experience_level}
                      onChange={(e) => setProfile({ ...profile, experience_level: e.target.value as 'beginner' | 'intermediate' | 'advanced' })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="beginner">Principiante</option>
                      <option value="intermediate">Intermedio</option>
                      <option value="advanced">Avanzado</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="career_goals" className="block text-sm font-medium text-foreground/80">
                    Objetivos profesionales
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="career_goals"
                      rows={3}
                      value={profile.career_goals}
                      onChange={(e) => setProfile({ ...profile, career_goals: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="¿Cuáles son tus objetivos profesionales a corto y largo plazo?"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80">
                    Habilidades
                  </label>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {profile.skills?.map((skill) => (
                        <span
                          key={skill}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-accent/10 text-accent"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(skill)}
                            className="ml-2 focus:outline-none"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        className="flex-1 px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="Añadir nueva habilidad"
                      />
                      <button
                        type="button"
                        onClick={handleAddSkill}
                        className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="dark-card p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Información de Contacto</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground/80">
                    Email profesional
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      id="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground/80">
                    Teléfono
                  </label>
                  <div className="mt-1">
                    <input
                      type="tel"
                      id="phone"
                      value={profile.phone || ''}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-foreground/80">
                    Ubicación
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="location"
                      value={profile.location || ''}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="Ciudad, País"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="linkedin" className="block text-sm font-medium text-foreground/80">
                    LinkedIn
                  </label>
                  <div className="mt-1">
                    <input
                      type="url"
                      id="linkedin"
                      value={profile.linkedin || ''}
                      onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="https://linkedin.com/in/tu-perfil"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-foreground/80">
                    Sitio web personal
                  </label>
                  <div className="mt-1">
                    <input
                      type="url"
                      id="website"
                      value={profile.website || ''}
                      onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="https://tu-sitio-web.com"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Guardar cambios
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;